import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * Pause component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconPause extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M2.60547 3.41526C2.60547 2.96749 2.96846 2.60449 3.41624 2.60449H7.31816C7.76594 2.60449 8.12893 2.96749 8.12893 3.41526V20.5837C8.12893 21.0315 7.76594 21.3945 7.31816 21.3945H3.41624C2.96846 21.3945 2.60547 21.0315 2.60547 20.5837V3.41526ZM3.41624 1.10449C2.14004 1.10449 1.10547 2.13906 1.10547 3.41526V20.5837C1.10547 21.8599 2.14004 22.8945 3.41624 22.8945H7.31816C8.59436 22.8945 9.62893 21.8599 9.62893 20.5837V3.41526C9.62893 2.13906 8.59436 1.10449 7.31816 1.10449H3.41624ZM15.8721 3.41526C15.8721 2.96749 16.2351 2.60449 16.6828 2.60449H20.5848C21.0325 2.60449 21.3955 2.96749 21.3955 3.41526V20.5837C21.3955 21.0315 21.0325 21.3945 20.5848 21.3945H16.6828C16.2351 21.3945 15.8721 21.0315 15.8721 20.5837V3.41526ZM16.6828 1.10449C15.4066 1.10449 14.3721 2.13906 14.3721 3.41526V20.5837C14.3721 21.8599 15.4066 22.8945 16.6828 22.8945H20.5848C21.861 22.8945 22.8955 21.8599 22.8955 20.5837V3.41526C22.8955 2.13906 21.861 1.10449 20.5848 1.10449H16.6828Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-pause') === undefined) {
  customElements.define('cat-icon-pause', BlocksIconPause);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-pause': BlocksIconPause;
  }
}
